import React, { useState } from 'react';
import '../styles/FAQ.css';
import '../index.css';

function FAQ() {
  const [expandedQuestions, setExpandedQuestions] = useState({});

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqData = [
    {
      question: "How does our AI-powered research tool work?",
      answer: (
        <>
          <p>InstantFlows uses advanced AI technology to generate synthetic survey responses. Our approach:</p>
          <ul>
            <li>Leverages large language models to simulate how different demographics might respond</li>
            <li>Creates virtual personas based on demographic parameters you select</li>
            <li>Generates responses that aim to reflect realistic opinions and preferences</li>
            <li>Provides analysis of patterns and trends across different segments</li>
          </ul>
          <p>As we're currently in beta, we're continuously improving our models based on user feedback.</p>
        </>
      )
    },
    {
      question: "What are the limitations of AI-generated survey responses?",
      answer: (
        <>
          <p>While our AI tool provides valuable rapid insights, it's important to understand its limitations regarding synthetic data:</p>
          <ul>
            <li>Synthetic responses are simulations based on available data, not actual human responses</li>
            <li>The quality of automated insights depends on how well you frame your survey questions</li>
            <li>Some responses may contain inconsistencies or unexpected answers due to the nature of AI generation</li>
            <li>Results work best for initial concept testing and directional insights</li>
            <li>For critical business decisions, we recommend using our tool alongside traditional research methods</li>
          </ul>
        </>
      )
    },
    {
      question: "Why use InstantFlows instead of general AI tools?",
      answer: (
        <>
          <p>While general AI tools like ChatGPT can provide some insights, InstantFlows offers specific advantages:</p>
          <ul>
            <li>Purpose-built for research with specialized prompts and analysis</li>
            <li>Ability to filter and customize virtual personas based on demographics</li>
            <li>Structured survey format that mimics traditional research approaches</li>
            <li>Visual analysis and patterns identification across responses</li>
            <li>Significantly faster than setting up and running your own AI research</li>
          </ul>
          <p>We focus specifically on making AI-powered research accessible and useful for quick testing and validation.</p>
        </>
      )
    },
    {
      question: "How do you create virtual personas?",
      answer: (
        <>
          <p>Our AI creates virtual personas using:</p>
          <ul>
            <li>Demographic parameters that you select (age, gender, income, etc.)</li>
            <li>Contextual information you provide about the research</li>
            <li>Background knowledge from our AI models about population characteristics</li>
          </ul>
          <p>These personas are synthetic representations and not actual individuals. The more context you provide, the more relevant the responses will be to your specific scenario.</p>
        </>
      )
    },
    {
      question: "How reliable are the AI-generated responses?",
      answer: (
        <>
          <p>As with any AI tool in beta, there are important considerations regarding reliability:</p>
          <ul>
            <li>Responses are best used for early-stage testing and directional insights</li>
            <li>The AI attempts to simulate realistic opinions but may not perfectly match real-world behavior</li>
            <li>Results can vary between runs due to the nature of AI generation</li>
            <li>We're continuously improving our models based on user feedback and testing</li>
            <li>For high-stakes decisions, we recommend validating findings with traditional research methods</li>
          </ul>
        </>
      )
    },
    {
      question: "What types of research works best with InstantFlows?",
      answer: (
        <>
          <p>Our AI research tool is particularly effective for:</p>
          <ul>
            <li><strong>Early concept validation:</strong> Quickly test new product or feature ideas</li>
            <li><strong>Message testing:</strong> Compare different marketing messages or value propositions</li>
            <li><strong>Target audience exploration:</strong> Understand preferences across different demographics</li>
            <li><strong>Rapid prototyping:</strong> Get feedback on designs or concepts before investing in development</li>
            <li><strong>A/B testing alternatives:</strong> Test different options to guide your decision-making</li>
          </ul>
          <p>Many users find value in running multiple small tests rather than one large research project.</p>
        </>
      )
    },
    {
      question: "What can I expect from the results and analysis?",
      answer: (
        <>
          <p>Our tool provides several types of outputs to help you make sense of the data:</p>
          <ul>
            <li>Individual AI-generated responses from each virtual persona</li>
            <li>Basic visualization of response distributions</li>
            <li>Demographic breakdown of different answer patterns</li>
            <li>Raw data export for further analysis in your preferred tools</li>
          </ul>
          <p>As we're in beta, we're continuously adding new analysis features based on user feedback. The quality of insights depends significantly on how well your research questions are formulated.</p>
        </>
      )
    },
    {
      question: "How can I get the best results from InstantFlows?",
      answer: (
        <>
          <p>To maximize the value of your AI research:</p>
          <ul>
            <li>Provide detailed context about your research goals in the description field</li>
            <li>Frame questions clearly and specifically to reduce ambiguity</li>
            <li>Select demographic parameters that match your target audience</li>
            <li>Run multiple research projects to test different angles or approaches</li>
            <li>Use our tool for rapid iteration and initial validation before investing in larger efforts</li>
          </ul>
          <p>During our beta phase, we welcome feedback on how we can improve the tool to better meet your needs.</p>
        </>
      )
    }
  ];
  
  return (
    <div className="faq-page">
      <div className="faq-hero">
        <h1 className="faq-hero-title">Frequently Asked Questions</h1>
      </div>
      <div className="faq-sections">
        {faqData.map((item, index) => (
          <div 
            key={index} 
            className={`faq-section ${expandedQuestions[index] ? 'expanded' : ''}`}
          >
            <div 
              className="faq-question"
              onClick={() => toggleQuestion(index)}
            >
              <h2>{item.question}</h2>
              <div className="faq-toggle" />
            </div>
            <div className="faq-answer">
              {item.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FAQ;